import { useState } from "react";
import { Dialog } from "@headlessui/react";
import { useForm } from "react-hook-form";

import FormInputError from "components/General/FormInputError";
import DialogBackdrop from "./DialogBackdrop";
import config from "config/config";
import { isAgentPortal, isFlagEnabled } from "js/utilities";
import { useTranslation, Trans } from "react-i18next";

export default function ContactUsDialog({ dialogState, setDialogState }) {
  const MAXIMUMWORDCOUNT = 350;
  const [currentWordCount, setCurrentWordCount] = useState(0);
  const [confirmDialogState, setConfirmDialogState] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [title, setTitle] = useState();
  const [description, setDescription] = useState();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetContactUsForm,
  } = useForm();

  const getEmailTitleByCategory = (category) => {
    const title = {
      billingAndPayment: t("UnAuthContactUs-label-3-option-1"),
      commissions: t("UnAuthContactUs-label-3-option-2"),
      policyInformation: t("UnAuthContactUs-label-3-option-3"),
      policyChanges: t("UnAuthContactUs-label-3-option-4"),
      claims: t("UnAuthContactUs-label-3-option-5"),
      beneficiaryVerification: t("UnAuthContactUs-label-3-option-6"),
      disbursements: t("UnAuthContactUs-label-3-option-7"),
      technicalAssistance: t("UnAuthContactUs-label-3-option-8"),
      escalations: t("UnAuthContactUs-label-3-option-10"),
    };

    return title[category];
  };

  async function submitWebToCase(data) {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    let salesforceURL = "";
    if (config.SFDC_VERSION === "PROD") {
      salesforceURL = `https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8&orgId=00Df400000488Mv`;
    } else {
      salesforceURL = `https://nttfsiclaims--nttfsiuat.sandbox.my.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8&orgId=00DcZ000000rXkE`;
    }
    // Map to Salesforce's Web-to-Case standard parameters
    const sfFields = {
      orgid:
        config.SFDC_VERSION === "PROD" ? "00Df400000488Mv" : "00DcZ000000rXkE",
      retURL: `https://${
        isAgentPortal()
          ? config?.AGENT_PORTAL_HOSTNAME
          : config?.CUSTOMER_PORTAL_HOSTNAME
      }/help`,
      email: data.email || "",
      subject: getEmailTitleByCategory(data?.category) || "",
      description: data?.message || "",
      external: "1",
      "00N5G00000UszjE": config.SFDC_VERSION === "PROD" ? "4Q" : "5Q",
      "00N5G00000UszjF": "EVL",
      origin: isAgentPortal() ? "EVL Portal Agent" : "EVL Portal Customer",
      recordType: "0125G000000JpEF",
    };
    // Build x-www-form-urlencoded
    const formBody = Object.entries(sfFields)
      .map(
        ([key, value]) =>
          encodeURIComponent(key) + "=" + encodeURIComponent(value)
      )
      .join("&");

    try {
      await fetch(salesforceURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody,
        mode: "no-cors",
      });
      setTitle(t("UnAuthContactUs-confirmation-title"));
      setDescription(t("UnAuthContactUs-confirmation-description"));
      setConfirmDialogState(true);
      resetContactUsForm();
      setCurrentWordCount(0); // RESET WORD COUNT
      setDialogState(false);
    } catch (error) {
      setTitle("Error");
      setDescription(t("UnAuthContactUs-error-1"));
      setConfirmDialogState(true);
      resetContactUsForm();
      setCurrentWordCount(0); // RESET WORD COUNT
      setDialogState(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Dialog
        open={dialogState}
        onClose={() => {
          setDialogState(false);
          resetContactUsForm();
          setCurrentWordCount(0); // RESET WORD COUNT
        }}
        className="ui-dialogs"
      >
        <DialogBackdrop />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              {t("UnAuthContactUs-title-1")}
            </Dialog.Title>
            <button
              className="ui-default-dialog-close"
              aria-label="close edit photo window"
              onClick={() => setDialogState(false)}
              disabled={isSubmitting}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
            <Dialog.Description>
              {t("UnAuthContactUs-subTitle-1")}
            </Dialog.Description>
            <hr />
            <form onSubmit={handleSubmit(submitWebToCase)} autoComplete="off">
              <div className="ui-dialog-body">
                {isAgentPortal() ? (
                  <div>
                    <label id="ID_emailLabel" htmlFor="ID_email">
                      {t("UnAuthContactUs-label-1")}
                    </label>
                    <input
                      id="ID_email"
                      type="text"
                      aria-labelledby="ID_emailLabel"
                      aria-describedby="ID_emailError"
                      className="mt-1"
                      {...register("email", {
                        required: t("UnAuthContactUs-error-2"),
                        pattern: {
                          value: /^[-_A-Za-z0-9\.]+@allstate\.com\s*$/,
                          message: t("UnAuthContactUs-error-3"),
                        },
                      })}
                    />
                    <FormInputError
                      id="ID_emailError"
                      errorMessage={errors?.email?.message}
                    />
                  </div>
                ) : (
                  <div>
                    <label id="ID_emailLabel" htmlFor="ID_email">
                      {t("UnAuthContactUs-label-1")}
                    </label>
                    <input
                      id="email"
                      maxlength="80"
                      name="email"
                      type="text"
                      aria-labelledby="ID_emailLabel"
                      aria-describedby="ID_emailError"
                      className="mt-1"
                      {...register("email", {
                        required: t("UnAuthContactUs-error-4"),
                        pattern: {
                          value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                          message: t("UnAuthContactUs-error-5"),
                        },
                      })}
                    />
                    <FormInputError
                      id="ID_emailError"
                      errorMessage={errors?.email?.message}
                    />
                  </div>
                )}
                <div className="mt-4">
                  <label id="subject" for="subject">
                    {t("UnAuthContactUs-label-2")}
                  </label>
                  <div>
                    <select
                      id="ID_emailSubject"
                      aria-labelledby="ID_emailCategoryLabel"
                      aria-describedby="ID_emailCategoryError"
                      className="mt-1"
                      {...register("category", {
                        required: t("UnAuthContactUs-error-6"),
                      })}
                    >
                      <option value="">{t("UnAuthContactUs-label-3")}</option>
                      <option value="billingAndPayment">
                        {t("UnAuthContactUs-label-3-option-1")}
                      </option>
                      {isAgentPortal() && (
                        <option value="commissions">
                          {t("UnAuthContactUs-label-3-option-2")}
                        </option>
                      )}

                      <option value="policyInformation">
                        {t("UnAuthContactUs-label-3-optn-3")}
                      </option>
                      <option value="policyChanges">
                        {t("UnAuthContactUs-label-3-optn-4")}
                      </option>
                      <option value="claims">
                        {t("UnAuthContactUs-label-3-option-5")}
                      </option>
                      <option value="beneficiaryVerification">
                        {t("UnAuthContactUs-label-3-option-6")}
                      </option>
                      <option value="disbursements">
                        {t("UnAuthContactUs-label-3-option-7")}
                      </option>
                      <option value="technicalAssistance">
                        {t("UnAuthContactUs-label-3-option-8")}
                      </option>
                      {isFlagEnabled("ENABLE_ESCALATION_ON_AGENT_PORTAL") &&
                        isAgentPortal() && (
                          <option value="escalations">
                            {t("UnAuthContactUs-label-3-option-10")}
                          </option>
                        )}
                    </select>
                    <FormInputError
                      id="ID_emailCategoryError"
                      errorMessage={errors?.category?.message}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label id="ID_messageLabel" for="description">
                    {t("UnAuthContactUs-label-4", {
                      MAXIMUMWORDCOUNT,
                    })}
                  </label>
                  <textarea
                    type="text"
                    name="description"
                    aria-labelledby="ID_messageLabel"
                    aria-describedby="ID_messageError"
                    className="mt-1"
                    rows="6"
                    {...register("message", {
                      required: t("UnAuthContactUs-error-7"),
                      maxLength: {
                        value: 350,
                        message: t("UnAuthContactUs-error-8"),
                      },
                      onChange: (e) => {
                        setCurrentWordCount(e.target.value.length);
                      },
                    })}
                  />
                  <span
                    className={
                      currentWordCount > MAXIMUMWORDCOUNT
                        ? "ui-input-error"
                        : ""
                    }
                  >
                    ({currentWordCount}/{MAXIMUMWORDCOUNT})
                  </span>
                  <FormInputError
                    id="ID_emailError"
                    errorMessage={errors?.message?.message}
                  />
                </div>
              </div>
              <div className="block text-right mt-6">
                <button
                  type="button"
                  className="ui-btn-secondary ui-btn-min-w mt-4"
                  onClick={() => {
                    setDialogState(false);
                    resetContactUsForm();
                  }}
                  disabled={isSubmitting}
                >
                  <Trans
                    i18nKey="UnAuthContactUs-button-1"
                    components={{
                      srText: <span className="sr-only"></span>,
                    }}
                  />
                </button>
                <button
                  className="ui-btn-primary ui-btn-min-w mt-4"
                  disabled={isSubmitting}
                >
                  <Trans
                    i18nKey="UnAuthContactUs-button-2"
                    components={{
                      srText: <span className="sr-only"></span>,
                    }}
                  />
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
      <Dialog
        open={confirmDialogState}
        onClose={() => setConfirmDialogState(false)}
        className="ui-dialogs"
      >
        <DialogBackdrop />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              {title}
            </Dialog.Title>
            <button
              className="ui-default-dialog-close"
              aria-label="close Policy Look-Up window"
              onClick={() => setConfirmDialogState(false)}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
            <hr />
            <Dialog.Description>{description}</Dialog.Description>

            <div className="block text-right mt-6">
              <button
                className="ui-btn-secondary ui-btn-min-w mt-4"
                onClick={() => setConfirmDialogState(false)}
              >
                <Trans
                  i18nKey="UnAuthContactUs-button-3"
                  components={{
                    srText: <span className="sr-only"></span>,
                  }}
                />
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
